import { useCallback, useEffect, useState } from 'react';
import { getGitHubActivity, getLinearIssues, GitHubActivity, SourceResult } from '@/services/systemHealth';
import { LinearIssue, linearIssues as dummyLinearIssues, mergedPrs, openPrs } from './activityDummyData';

const POLL_MS = 60_000;

const dummyRepos = [...new Set([...openPrs, ...mergedPrs].map((p) => p.repo))].map((name) => ({
    name,
    description: null,
    defaultBranch: 'master',
    pushedAt: null,
}));
const dummyGitHub: GitHubActivity = { openPrs, mergedPrs, repos: dummyRepos };

export interface Source<T> {
    data: T;
    /** False while showing dummy data: the source failed, isn't configured, or hasn't answered yet. */
    live: boolean;
    /** Real data, but the latest poll failed (e.g. an expired token): what's shown is from an earlier poll. */
    stale: boolean;
    error: string | null;
}

/** Last good answer wins; dummy data only until a source has answered once. */
export function useSource<T>(load: () => Promise<SourceResult<T>>, dummy: T): [Source<T>, () => void] {
    const [result, setResult] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
    const reload = useCallback(
        () => load().then((r) => setResult((prev) => ({ data: r.data ?? prev.data, error: r.error }))),
        [load]
    );
    useEffect(() => {
        reload();
        const id = setInterval(reload, POLL_MS);
        return () => clearInterval(id);
    }, [reload]);
    const live = result.data != null;
    return [{ data: result.data ?? dummy, live, stale: live && result.error != null, error: result.error }, reload];
}

/** GitHub PRs and Linear issues for the Activity and Customers tabs, polled once a minute. */
export function useDevActivity() {
    const [github, reloadGitHub] = useSource(getGitHubActivity, dummyGitHub);
    const [linear, reloadLinear] = useSource(getLinearIssues, dummyLinearIssues);
    return { github, linear, reloadGitHub, reloadLinear };
}

/** In-flight states in board order, then Done. */
export function linearColumns(issues: LinearIssue[]): string[] {
    const started = [...new Map(issues.filter((i) => i.stateType === 'started').map((i) => [i.state, i])).values()]
        .sort((a, b) => a.statePosition - b.statePosition)
        .map((i) => i.state);
    return [...started, 'Done'];
}

export const isDone = (i: LinearIssue) => i.stateType === 'completed';
// ponytail: matched by name since Linear has no "ready to test" category; rename here if the state is renamed
export const isReadyToTest = (i: LinearIssue) => /ready.*test/i.test(i.state);
/** Past development: in review, testing or waiting for deploy. Matched by name, like isReadyToTest. */
export const isComing = (i: LinearIssue) => !isDone(i) && /review|test|deploy/i.test(i.state);
